import { FC, useState } from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { Colors } from "../../../../configs";
import { ICustomerBill } from "../../../../typings";
import {
  formatCashierColumn,
  formatCurrency,
  formatDateShort,
} from "../../../../util/common-functions";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { getBillItemIdListAction } from "../../../../redux/actions/cartAction";
import { SxProps, Theme } from "@mui/material/styles";

export interface ICustomerHistoryTableRowProps {
  data: ICustomerBill;
  onClick(data: ICustomerBill): void;
  onViewClick(data: ICustomerBill): void;
  firstRow: boolean;
  lastRow: boolean;
  isSelected: boolean;
}

const CustomerHistoryTableRow: FC<ICustomerHistoryTableRowProps> = ({
  data,
  firstRow,
  lastRow,
  onClick,
  onViewClick,
  isSelected,
}) => {
  const { cartHistorySelectedRows, billById, billItemIdList } = useAppSelector(
    (state) => state.cart
  );
  const dispatch = useAppDispatch();

  const selectedRow = cartHistorySelectedRows.some(
    (row) => row.billId === data.billId
  );

  const [hovered, setHovered] = useState(false);

  const isVoidedRow = Boolean(data.isVoided);
  const isRefundedRow = Boolean(data.isRefunded);

  const useRefundStyle = isRefundedRow;
  const useVoidStyle = !useRefundStyle && isVoidedRow;
  const isSpecialRow = useRefundStyle || useVoidStyle;

  const baseBg = selectedRow ? `${Colors.SECONDARY}14` : Colors.WHITE;
  const hoverBg = `${Colors.SECONDARY}0D`;
  const effectiveBg = hovered && !selectedRow ? hoverBg : baseBg;
  const rowTextColor = isSelected ? Colors.SECONDARY : Colors.TEXT_GRAY_DARK;

  const typographyCommonSx = {
    color: rowTextColor,
    fontWeight: "bold",
  };

  // Pieces counts physical garments: every item contributes its qty, not 1.
  // totalPieceCount comes from the API pre-summed; totalItemCount (line-item
  // count) is only a fallback for responses that predate it.
  const pieceCount =
    data.totalPieceCount ?? data.totalItemCount ?? "N/A";

  // billAmount is already net of the discount (check-in + checkout), so the
  // discount must not come off again.
  const balanceDue = Math.max(0, (data.billAmount || 0) - (data.paidAmount ?? 0));
  const balanceColor = balanceDue > 0 ? Colors.BROWN : rowTextColor;
  const invoiceColor = Colors.SECONDARY;

  const invoiceSx = { ...typographyCommonSx, color: invoiceColor };
  const semiBoldSx = { ...typographyCommonSx, fontWeight: 600 };
  const regularSx = { ...typographyCommonSx, fontWeight: 400 };

  const AMBER = "#C98900";
  const getStatusInfo = () => {
    if (isRefundedRow) return { label: "Refunded", color: Colors.BROWN };
    if (isVoidedRow) return { label: "Voided", color: Colors.BROWN };
    if (data.pickingFilterStatus === "PICKED_UP") {
      return { label: "Picked Up", color: Colors.PRIMARY };
    }
    if (data.pickingFilterStatus === "PARTIALLY_PICKED_UP") {
      return { label: "Partially PU", color: AMBER };
    }
    return { label: "In Store", color: Colors.MAIN_GRAY };
  };
  const statusInfo = getStatusInfo();

  const selectRow = () => {
    if (selectedRow) {
      dispatch(
        CartSliceAction.setBillItemIdList(
          billItemIdList.filter((item) => item.billId !== data.id)
        )
      );
      dispatch(
        CartSliceAction.setCartHistorySelectedRows(
          cartHistorySelectedRows.filter((row) => row.billId !== data.billId)
        )
      );
    } else {
      dispatch(getBillItemIdListAction(data.id));
      dispatch(
        CartSliceAction.setCartHistorySelectedRows([
          ...cartHistorySelectedRows,
          data,
        ])
      );
    }
  };

  const checkboxSx: SxProps<Theme> = !isVoidedRow
    ? {
        "&.Mui-checked": { color: "success.main" },
      }
    : {
        "&.MuiCheckbox-root": {
          backgroundColor: Colors.ACCENT_GRAY,
          borderRadius: "4px",
        },
        "&.Mui-checked": {
          backgroundColor: Colors.ACCENT_GRAY,
          color: Colors.ACCENT_GRAY,
        },
      };

  let styles = {
    tableBodyCell: {
      background: effectiveBg,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      padding: "1.14rem 0.28rem",
      paddingBottom: "0rem",
      boxSizing: "border-box",
      gap: "1.07rem",
      color: rowTextColor,
      fontWeight: "bold",
      borderBottom: "1px solid #E8E8E8",
      zIndex: 0,
    },
    descriptionCell: {
      alignItems: "center",
      zIndex: 0,
    },
    tableRow: {
      zIndex: 0,
    },
  };

  const handleOnClickRow = () => {
    onClick(data);
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.15rem",
        flexWrap: "nowrap",
        marginBottom: "0.15rem",
        opacity: isSpecialRow ? 0.45 : 1,
        zIndex: 0,
      }}
      onClick={handleOnClickRow}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        sx={{
          ...styles.tableBodyCell,

          ...(lastRow ? { borderBottomLeftRadius: "0.71rem" } : {}),
          flex: 1,
          minWidth: { lg: "2.92857142857rem", md: "2.8rem", sm: "2.92rem" },
          maxWidth: {
            lg: "2.92857142857rem !important",
            md: "2.8rem !important",
            sm: "2.92rem !important",
          },
          width: "3.246rem !important",
          boxSizing: "border-box !important",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          {isVoidedRow ? (
            <Checkbox
              disabled
              checked={false}
              sx={{
                "&.Mui-disabled": {
                  color: Colors.ACCENT_GRAY,
                },
              }}
            />
          ) : (
            <Checkbox
              onChange={selectRow}
              checked={selectedRow}
              sx={{
                ...checkboxSx,
              }}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,
          ...styles.descriptionCell,

          flex: 1,
          minWidth: { lg: "6.4rem", md: "6.4rem", sm: "6.4rem" },
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={invoiceSx}>{data.billId}</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "6.2rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={semiBoldSx}>
            {formatDateShort(data.createdTime)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: { lg: "5.5rem", md: "5.5rem" },
          padding: "0.5rem 0.71rem",
        }}
      >
        {/* cashier column */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={regularSx}>
            {formatCashierColumn(data.cashierName, data.pickupCashierName)}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "5.9rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={regularSx}>
            {data.dueDate ? formatDateShort(data.dueDate) : "-"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "5.606rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={typographyCommonSx}>{pieceCount}</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "4.606rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={regularSx}>
            {data.rackNumber || "-"}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "5.5rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={{ ...typographyCommonSx, color: balanceColor }}>
            {formatCurrency({
              amount: balanceDue,
              prefix: "$",
            })}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "5.9rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={regularSx}>
            {balanceDue === 0 && (data.paidAmount ?? 0) > 0
              ? formatDateShort(data.lastPaymentDate)
              : "-"}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "5.9rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            paddingBottom: "0.5rem",
            borderBottom: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "-0.0714285715rem",
            zIndex: 1,
          }}
        >
          <Typography sx={regularSx}>
            {data.lastPickupDate ? formatDateShort(data.lastPickupDate) : "N/A"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1,
          minWidth: "6.2rem",
          padding: "0.5rem 0.71rem",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: "999px",
              border: `1px solid ${statusInfo.color}40`,
              backgroundColor: `${statusInfo.color}1A`,
              padding: "0.21rem 0.64rem",
            }}
          >
            <Typography
              sx={{
                color: statusInfo.color,
                fontWeight: "bold",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
              }}
            >
              {statusInfo.label}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          ...styles.tableBodyCell,

          flex: 1.5,
          minWidth: "5.246rem",
          maxWidth: "5.246rem !important",
          padding: "0.36rem",
          ...(lastRow ? { borderBottomRightRadius: "0.71rem" } : {}),
        }}
      >
        <Box
          onClick={() => {
            onViewClick(data);
          }}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "0.5rem",
            backgroundColor: "transparent",
            transition: "opacity 0.15s ease",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        >
          <VisibilityIcon sx={{ fontSize: "1.14rem", color: Colors.SECONDARY }} />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerHistoryTableRow;
